import { Injectable } from '@angular/core';
import { ObservableBoolean } from '@studiohyperdrive/rxjs-utils';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, tap } from 'rxjs/operators';

import { ELoketEndpoints } from '@vlaio/shared/endpoints';

import { AppStatusEntity, AppStatusResultEntity } from '../interfaces';

import { BrowserService } from './browser.service';
import { VlaioHttpClientService } from './vlaio-http-client.service';

@Injectable()
export class StatusService {
	/**
	 * The key we use to save the app status errors in
	 */
	private readonly appStatusErrorKey = 'VLAIO-APP-STATUS-ERROR';

	/**
	 * The subject to hold the status of the application
	 */
	private readonly statusSubject$ = new BehaviorSubject<AppStatusEntity>({
		isActive: true
	});

	/**
	 * Current status of the application
	 */
	public readonly status$: ObservableBoolean = this.statusSubject$
		.asObservable()
		.pipe(map(({ isActive }) => isActive));

	constructor(
		private readonly browserService: BrowserService,
		private readonly httpClient: VlaioHttpClientService
	) {}

	/**
	 * Forces the application to clear the available cache and reload the ServiceWorker is needed.
	 *
	 * @param shouldHaveServiceWorker - If set to `true` (default), the cache will only be cleared if a Service
	 * Worker is present. When set to `false`, the cache will be cleared regardless of the Service Worker.
	 */
	public forceFlushCache(shouldHaveServiceWorker: boolean = true): Observable<void> {
		return this.clearAppCache(shouldHaveServiceWorker).pipe(
			filter(Boolean),
			map(() => this.browserService.refresh())
		);
	}

	/**
	 * Clear the browser and Service Worker cache.
	 *
	 * This will force the application to reload all assets.
	 *
	 * @param shouldHaveServiceWorker - Whether the application should have a Service Worker.
	 * - If set to `true` (default), possible Service Workers will be unregistered and all cache will be cleared.
	 * **Important** to note that if no Service Worker is present, no cache will be cleared. In such cases,
	 * this method will do essentially nothing.
	 * - If set to `false`, all cache will be cleared.
	 *
	 * @description Wouter: As of March 2025, the Service Worker is no longer shipped with the application.
	 * The issue lies in the fact that a client can possibly have an older version of the app in which the
	 * Service Worker is still active. This can cause issues with the application not updating correctly.
	 * This is why we clear the Service Worker cache as well.
	 *
	 * @returns An observable that emits `true` if the page needs reloading, `false` otherwise.
	 */
	private clearAppCache(shouldHaveServiceWorker: boolean = true): ObservableBoolean {
		return this.browserService.runInBrowser(({ browserWindow }) => {
			// Wouter: If we want to clear the cache regardless of the Service Worker, we do so and exit
			if (!shouldHaveServiceWorker) {
				this.handleCacheFlush(browserWindow);
				// Wouter: Reload the page
				return of(true);
			}

			// Wouter: Handle cleanup of the Service Worker and its cache if it exists
			if ('serviceWorker' in navigator) {
				navigator.serviceWorker.getRegistrations().then((registrations) => {
					// Wouter: If no Service Worker was found, we do not need to reload the page
					if (!registrations.length) {
						return of(false);
					}

					// Wouter: Unregister all Service Workers
					for (const registration of registrations) {
						// Wouter: Unregister all Service Workers
						registration.unregister().then((success) => {
							if (success) {
								console.log('Service Worker unregistered.');
							}
						});
					}

					// Wouter: If at least one ServiceWorker still existed, we clear all cache
					this.handleCacheFlush(browserWindow);

					// Wouter: Reload the page
					return of(true);
				});
			}

			// Wouter: If no Service Worker was found, we do not need to reload the page
			return of(false);
		});
	}

	/**
	 * Clear the cache of the application
	 *
	 * @param window - The window object
	 */
	private handleCacheFlush(window: Window): void {
		if (window && 'caches' in window) {
			caches.keys().then((cacheNames) => {
				cacheNames.forEach((cacheName) => caches.delete(cacheName));
			});
		}
	}

	/**
	 * Get the current status of the application
	 */
	public getAppStatus(onError?: (err: string) => void): Observable<AppStatusEntity> {
		// Iben: Check if there was an error in the previous app status call
		this.browserService.runInBrowser(() => {
			const error = localStorage.getItem(this.appStatusErrorKey);

			// Iben: If there was an error, we run the error callback and remove the item
			if (error) {
				onError(error);

				localStorage.removeItem(this.appStatusErrorKey);
			}
		});

		return this.httpClient.get<AppStatusResultEntity>(ELoketEndpoints.Status.Status()).pipe(
			map((result) => {
				// Iben: If the call succeeds, the application is available
				return {
					isActive: !result.onderhoudsmodus,
					message: result.onderhoudsmodusBericht
				};
			}),
			tap((result) => {
				// Iben: If the call succeeds, the application is available
				this.setStatus(result);
			}),
			catchError((err: Error) => {
				// Iben: If the call fails, the application is unavailable
				this.setStatus({
					isActive: false
				});

				// Iben: Set the error in the local storage, so we can track it next time the application loads
				this.browserService.runInBrowser(() => {
					localStorage.setItem(this.appStatusErrorKey, err.message);
				});

				return throwError(err);
			})
		);
	}

	/**
	 * Set the current status of the application
	 *
	 * @param status - The status of the application
	 */
	public setStatus(status: AppStatusEntity) {
		this.statusSubject$.next(status);
	}

	/**
	 * Returns the status message
	 */
	public get statusMessage(): string {
		return this.statusSubject$.getValue().message;
	}
}
